import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
type FormData = {
  // Section A
  legalCompanyName: string;
  tradingName: string;
  companyRegistrationNumber: string;
  vatTaxNumber: string;
  yearEstablished: string;
  countryOfRegistration: string;
  registeredAddress: string;
  website: string;
  mainContactPerson: string;
  position: string;
  emailAddress: string;
  telephoneNumber: string;
  // Section B
  businessActivities: string;
  productCategories: string;
  supplyStructure: string;
  productionCapacity: string;
  // Section C
  currentExportMarkets: string;
  existingClients: string;
  exportDocCapability: string;
  // Section D
  certifications: string[];
  otherCertifications: string;
  qualityControlProcedures: string;
  // Section E
  pricingTerms: string[];
  standardLeadTime: string;
  moq: string;
  packagingCapabilities: string;
  logisticsCapability: string;
  // Section F
  authorisedRepresentative: string;
  representativePosition: string;
  typedSignature: string;
  confirmAccurate: boolean;
  consentGdpr: boolean;
  consentMarketing: boolean;
};
const initialFormData: FormData = {
  legalCompanyName: '',
  tradingName: '',
  companyRegistrationNumber: '',
  vatTaxNumber: '',
  yearEstablished: '',
  countryOfRegistration: '',
  registeredAddress: '',
  website: '',
  mainContactPerson: '',
  position: '',
  emailAddress: '',
  telephoneNumber: '',
  businessActivities: '',
  productCategories: '',
  supplyStructure: '',
  productionCapacity: '',
  currentExportMarkets: '',
  existingClients: '',
  exportDocCapability: '',
  certifications: [],
  otherCertifications: '',
  qualityControlProcedures: '',
  pricingTerms: [],
  standardLeadTime: '',
  moq: '',
  packagingCapabilities: '',
  logisticsCapability: '',
  authorisedRepresentative: '',
  representativePosition: '',
  typedSignature: '',
  confirmAccurate: false,
  consentGdpr: false,
  consentMarketing: false
};
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const SUPPLIER_ACCESS_KEY = '7f291248-54db-405a-948d-aee1147c1197';
export function SupplierRegistration() {
  useSEO({
    title: 'Supplier Pre-Qualification & Capability Assessment Form',
    description: 'Submit your pre-qualification and capability assessment to be considered for institutional and commercial procurement opportunities through 888 Sourcing Ltd.',
    path: '/supplier-registration'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const handleMultiCheckbox = (field: 'certifications' | 'pricingTerms', value: string) => {
    setFormData(prev => {
      const current = prev[field];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return {
        ...prev,
        [field]: updated
      };
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    // Flatten array and boolean fields for cleaner email formatting
    const payload = {
      access_key: SUPPLIER_ACCESS_KEY,
      subject: `New supplier pre-qualification: ${formData.legalCompanyName}`,
      from_name: '888 Sourcing Website',
      botcheck: '',
      ...formData,
      certifications: formData.certifications.join(', '),
      pricingTerms: formData.pricingTerms.join(', '),
      confirmAccurate: formData.confirmAccurate ? 'Yes' : 'No',
      consentGdpr: formData.consentGdpr ? 'Yes' : 'No',
      consentMarketing: formData.consentMarketing ? 'Yes' : 'No'
    };
    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success) {
        setIsSuccess(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        setErrorMessage(data?.message || 'Something went wrong submitting your form. Please try again or email us directly at info@888sourcing.co.uk.');
      }
    } catch (err) {
      setErrorMessage('Unable to submit at the moment. Please check your connection or email us directly at info@888sourcing.co.uk.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-navy-dark border border-slate-700 rounded-md px-4 py-2.5 text-white focus:outline-none focus:border-gold transition-colors';
  if (isSuccess) {
    return <div className="pt-24 pb-24 bg-navy-dark min-h-screen flex items-center justify-center px-6" data-id="element-759">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-navy-light border border-slate-800 p-12 rounded-lg max-w-2xl w-full text-center" data-id="element-760">
          <div className="w-20 h-20 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-761">
            <CheckCircleIcon size={40} className="text-green-500" data-id="element-762" />
          </div>
          <h2 className="text-3xl font-semibold text-white mb-4" data-id="element-763">
            Submission Received
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-8" data-id="element-764">
            Thank you. Your pre-qualification submission has been received. Our
            procurement team will review your profile and contact you regarding
            next steps where appropriate.
          </p>
          <button onClick={() => {
          setIsSuccess(false);
          setFormData(initialFormData);
        }} className="bg-gold hover:bg-gold-dark text-navy-dark px-8 py-3 rounded-md font-semibold transition-colors" data-id="element-765">
            Submit Another Form
          </button>
        </motion.div>
      </div>;
  }
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-766">
      {/* Hero Band */}
      <section className="py-16 px-6 bg-navy-light border-b border-slate-800" data-id="element-767">
        <div className="max-w-4xl mx-auto text-center" data-id="element-768">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-769">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-770">
              <div className="w-12 h-px bg-gold" data-id="element-771"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-772">
                Pre-Qualification
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-773"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white mb-6" data-id="element-774">
              Supplier Pre-Qualification & Capability Assessment Form
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-775">
              Thank you for your interest in participating in our supplier
              evaluation and procurement sourcing programmes. Please complete
              the form below — sections marked * are required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 px-6" data-id="element-776">
        <div className="max-w-4xl mx-auto" data-id="element-777">
          <div className="bg-navy-light border-l-4 border-gold rounded-r-lg p-6 mb-8" data-id="element-778">
            <h2 className="text-lg font-semibold text-white mb-2" data-id="element-779">
              Prefer to complete the form offline?
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-4" data-id="element-780">
              Download the print-ready form, complete it digitally or by hand,
              then email the completed document to info@888sourcing.co.uk.
            </p>
            <a href="/supplier-pre-qualification-pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-gold hover:bg-gold-dark text-navy-dark px-4 py-2.5 rounded-md font-semibold text-sm transition-colors" data-id="element-781">
              Open Print-Ready Form
            </a>
          </div>

          {errorMessage && <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-4 rounded-md mb-8" data-id="element-782">
              {errorMessage}
            </div>}

          <form onSubmit={handleSubmit} className="space-y-12" data-id="element-783">
            {/* SECTION A */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-784">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-785">
                SECTION A – COMPANY INFORMATION
              </h2>
              <div className="space-y-6" data-id="element-786">
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-787">
                  <div data-id="element-788">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-789">
                      Legal Company Name *
                    </label>
                    <input type="text" name="legalCompanyName" required value={formData.legalCompanyName} onChange={handleChange} className={inputClass} data-id="element-790" />
                  </div>
                  <div data-id="element-791">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-792">
                      Trading Name
                    </label>
                    <input type="text" name="tradingName" value={formData.tradingName} onChange={handleChange} className={inputClass} data-id="element-793" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-794">
                  <div data-id="element-795">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-796">
                      Company Registration Number *
                    </label>
                    <input type="text" name="companyRegistrationNumber" required value={formData.companyRegistrationNumber} onChange={handleChange} className={inputClass} data-id="element-797" />
                  </div>
                  <div data-id="element-798">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-799">
                      VAT / Tax Registration Number
                    </label>
                    <input type="text" name="vatTaxNumber" value={formData.vatTaxNumber} onChange={handleChange} className={inputClass} data-id="element-800" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-801">
                  <div data-id="element-802">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-803">
                      Year Established
                    </label>
                    <input type="text" name="yearEstablished" value={formData.yearEstablished} onChange={handleChange} className={inputClass} data-id="element-804" />
                  </div>
                  <div data-id="element-805">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-806">
                      Country of Registration *
                    </label>
                    <input type="text" name="countryOfRegistration" required value={formData.countryOfRegistration} onChange={handleChange} className={inputClass} data-id="element-807" />
                  </div>
                </div>

                <div data-id="element-808">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-809">
                    Registered Address *
                  </label>
                  <textarea name="registeredAddress" required rows={3} value={formData.registeredAddress} onChange={handleChange} className={inputClass} data-id="element-810" />
                </div>

                <div data-id="element-811">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-812">
                    Website
                  </label>
                  <input type="text" name="website" placeholder="e.g. www.example.com" value={formData.website} onChange={handleChange} className={inputClass} data-id="element-813" />
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-814">
                  <div data-id="element-815">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-816">
                      Main Contact Person *
                    </label>
                    <input type="text" name="mainContactPerson" required value={formData.mainContactPerson} onChange={handleChange} className={inputClass} data-id="element-817" />
                  </div>
                  <div data-id="element-818">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-819">
                      Position *
                    </label>
                    <input type="text" name="position" required value={formData.position} onChange={handleChange} className={inputClass} data-id="element-820" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-821">
                  <div data-id="element-822">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-823">
                      Email Address *
                    </label>
                    <input type="email" name="emailAddress" required value={formData.emailAddress} onChange={handleChange} className={inputClass} data-id="element-824" />
                  </div>
                  <div data-id="element-825">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-826">
                      Telephone Number *
                    </label>
                    <input type="text" name="telephoneNumber" required value={formData.telephoneNumber} onChange={handleChange} className={inputClass} data-id="element-827" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* SECTION B */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-828">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-829">
                SECTION B – BUSINESS PROFILE
              </h2>
              <div className="space-y-6" data-id="element-830">
                <div data-id="element-831">
                  <label className="block text-sm font-medium text-slate-300 mb-1" data-id="element-832">
                    Business Activities *
                  </label>
                  <p className="text-xs text-slate-400 mb-3" data-id="element-833">
                    Please describe your company's primary business activities.
                  </p>
                  <textarea name="businessActivities" required rows={4} value={formData.businessActivities} onChange={handleChange} className={inputClass} data-id="element-834" />
                </div>

                <div data-id="element-835">
                  <label className="block text-sm font-medium text-slate-300 mb-1" data-id="element-836">
                    Product Categories *
                  </label>
                  <p className="text-xs text-slate-400 mb-3" data-id="element-837">
                    Please list your primary product categories.
                  </p>
                  <textarea name="productCategories" required rows={3} value={formData.productCategories} onChange={handleChange} className={inputClass} data-id="element-838" />
                </div>

                <div data-id="element-839">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-840">
                    Manufacturing / Supply Structure *
                  </label>
                  <select name="supplyStructure" required value={formData.supplyStructure} onChange={handleChange} className={inputClass} data-id="element-841">
                    <option value="" data-id="element-842">Select structure...</option>
                    <option value="Manufacturer" data-id="element-843">Manufacturer</option>
                    <option value="Brand Owner" data-id="element-844">Brand Owner</option>
                    <option value="Exporter" data-id="element-845">Exporter</option>
                    <option value="Distributor" data-id="element-846">Distributor</option>
                    <option value="OEM" data-id="element-847">OEM</option>
                    <option value="Other" data-id="element-848">Other</option>
                  </select>
                </div>

                <div data-id="element-849">
                  <label className="block text-sm font-medium text-slate-300 mb-1" data-id="element-850">
                    Production Capacity
                  </label>
                  <p className="text-xs text-slate-400 mb-3" data-id="element-851">
                    Please provide estimated monthly production capacity.
                  </p>
                  <textarea name="productionCapacity" rows={2} value={formData.productionCapacity} onChange={handleChange} className={inputClass} data-id="element-852" />
                </div>
              </div>
            </motion.div>

            {/* SECTION C */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-853">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-854">
                SECTION C – EXPORT & INTERNATIONAL EXPERIENCE
              </h2>
              <div className="space-y-6" data-id="element-855">
                <div data-id="element-856">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-857">
                    Current Export Markets
                  </label>
                  <textarea name="currentExportMarkets" rows={2} value={formData.currentExportMarkets} onChange={handleChange} className={inputClass} data-id="element-858" />
                </div>

                <div data-id="element-859">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-860">
                    Existing Retail / Institutional Clients
                  </label>
                  <textarea name="existingClients" rows={2} value={formData.existingClients} onChange={handleChange} className={inputClass} data-id="element-861" />
                </div>

                <div data-id="element-862">
                  <label className="block text-sm font-medium text-slate-300 mb-1" data-id="element-863">
                    Export Documentation Capability
                  </label>
                  <p className="text-xs text-slate-400 mb-3" data-id="element-864">
                    Describe your capability to provide export documentation
                    (e.g. Certificate of Origin, Commercial Invoice, Packing
                    List, etc.)
                  </p>
                  <textarea name="exportDocCapability" rows={3} value={formData.exportDocCapability} onChange={handleChange} className={inputClass} data-id="element-865" />
                </div>
              </div>
            </motion.div>

            {/* SECTION D */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-866">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-867">
                SECTION D – PRODUCT & QUALITY INFORMATION
              </h2>
              <div className="space-y-6" data-id="element-868">
                <div data-id="element-869">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-870">
                    Certifications
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" data-id="element-871">
                    {['ISO', 'HACCP', 'BRC', 'CE', 'FDA', 'Organic', 'Halal', 'Other'].map(cert => <label key={cert} className="flex items-center gap-2 cursor-pointer" data-id="element-872">
                        <input type="checkbox" checked={formData.certifications.includes(cert)} onChange={() => handleMultiCheckbox('certifications', cert)} className="w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark" data-id="element-873" />
                        <span className="text-sm text-slate-300" data-id="element-874">{cert}</span>
                      </label>)}
                  </div>
                  {formData.certifications.includes('Other') && <input type="text" name="otherCertifications" placeholder="Please specify other certifications..." value={formData.otherCertifications} onChange={handleChange} className={inputClass} data-id="element-875" />}
                </div>

                <div data-id="element-876">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-877">
                    Quality Control Procedures
                  </label>
                  <textarea name="qualityControlProcedures" rows={3} value={formData.qualityControlProcedures} onChange={handleChange} className={inputClass} data-id="element-878" />
                </div>
              </div>
            </motion.div>

            {/* SECTION E */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-879">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-880">
                SECTION E – COMMERCIAL & LOGISTICS INFORMATION
              </h2>
              <div className="space-y-6" data-id="element-881">
                <div data-id="element-882">
                  <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-883">
                    Pricing Terms Available *
                  </label>
                  <div className="flex flex-wrap gap-6" data-id="element-884">
                    {['EXW', 'FOB', 'CIF', 'DAP', 'DDP'].map(term => <label key={term} className="flex items-center gap-2 cursor-pointer" data-id="element-885">
                        <input type="checkbox" checked={formData.pricingTerms.includes(term)} onChange={() => handleMultiCheckbox('pricingTerms', term)} className="w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark" data-id="element-886" />
                        <span className="text-sm text-slate-300" data-id="element-887">{term}</span>
                      </label>)}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-888">
                  <div data-id="element-889">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-890">
                      Standard Lead Time
                    </label>
                    <input type="text" name="standardLeadTime" value={formData.standardLeadTime} onChange={handleChange} className={inputClass} data-id="element-891" />
                  </div>
                  <div data-id="element-892">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-893">
                      Minimum Order Quantity (MOQ)
                    </label>
                    <input type="text" name="moq" value={formData.moq} onChange={handleChange} className={inputClass} data-id="element-894" />
                  </div>
                </div>

                <div data-id="element-895">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-896">
                    Packaging Capabilities
                  </label>
                  <textarea name="packagingCapabilities" rows={2} value={formData.packagingCapabilities} onChange={handleChange} className={inputClass} data-id="element-897" />
                </div>

                <div data-id="element-898">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-899">
                    Logistics & Shipping Capability
                  </label>
                  <textarea name="logisticsCapability" rows={2} value={formData.logisticsCapability} onChange={handleChange} className={inputClass} data-id="element-900" />
                </div>
              </div>
            </motion.div>

            {/* SECTION F */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy-light border border-slate-800 rounded-lg p-8" data-id="element-901">
              <h2 className="text-xl font-semibold text-gold mb-6 border-b border-slate-800 pb-4" data-id="element-902">
                SECTION F – COMPLIANCE & DECLARATION
              </h2>
              <div className="space-y-6" data-id="element-903">
                <p className="text-slate-300 italic mb-6" data-id="element-904">
                  We hereby confirm that the information provided is accurate to
                  the best of our knowledge.
                </p>

                <div className="grid sm:grid-cols-2 gap-6" data-id="element-905">
                  <div data-id="element-906">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-907">
                      Authorised Representative (Full Name) *
                    </label>
                    <input type="text" name="authorisedRepresentative" required value={formData.authorisedRepresentative} onChange={handleChange} className={inputClass} data-id="element-908" />
                  </div>
                  <div data-id="element-909">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-910">
                      Position *
                    </label>
                    <input type="text" name="representativePosition" required value={formData.representativePosition} onChange={handleChange} className={inputClass} data-id="element-911" />
                  </div>
                </div>

                <div data-id="element-912">
                  <label className="block text-sm font-medium text-slate-300 mb-1" data-id="element-913">
                    Authorised Signature (type your full name) *
                  </label>
                  <input type="text" name="typedSignature" required value={formData.typedSignature} onChange={handleChange} className={inputClass} data-id="element-914" />
                  <p className="text-xs text-slate-400 mt-2" data-id="element-915">
                    By typing my full name above, I confirm I am authorised to
                    submit this information on behalf of the company and that
                    all details provided are accurate.
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-800" data-id="element-916">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-917">
                    <input type="checkbox" name="confirmAccurate" required checked={formData.confirmAccurate} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark" data-id="element-918" />
                    <span className="text-sm text-slate-300" data-id="element-919">
                      I confirm that the information provided is accurate to the
                      best of my knowledge. *
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-920">
                    <input type="checkbox" name="consentGdpr" required checked={formData.consentGdpr} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark" data-id="element-921" />
                    <span className="text-sm text-slate-300" data-id="element-922">
                      I consent to 888 Sourcing Ltd processing this information
                      in accordance with the{' '}
                      <Link to="/privacy-policy" className="text-gold hover:underline" target="_blank" data-id="element-923">
                        Privacy Policy
                      </Link>
                      . *
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-924">
                    <input type="checkbox" name="consentMarketing" checked={formData.consentMarketing} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-slate-700 bg-navy-dark text-gold focus:ring-gold focus:ring-offset-navy-dark" data-id="element-925" />
                    <span className="text-sm text-slate-300" data-id="element-926">
                      I would like to receive procurement updates and
                      opportunity alerts relevant to my company's capabilities.
                    </span>
                  </label>
                </div>
              </div>
            </motion.div>

            {/* Submit Button & Note */}
            <div className="space-y-6" data-id="element-927">
              <p className="text-sm text-slate-400 text-center" data-id="element-928">
                If your submission progresses to evaluation, our team will
                contact you to request supporting documents (e.g. company
                registration, certifications, product catalogues). Please do not
                attach documents to this form.
              </p>
              <button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-gold-dark disabled:bg-gold/50 disabled:cursor-not-allowed text-navy-dark px-8 py-4 rounded-md font-semibold text-lg transition-colors flex items-center justify-center gap-2" data-id="element-929">
                {isSubmitting ? 'Submitting...' : 'Submit Pre-Qualification'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>;
}